import React, { useEffect, useRef } from 'react';
import './live-text-box.css';
// import { LIVE_TEXT_FEATURE_CONTROLLER } from '../../../controller/LiveTextController';
// import { addBgStyle, hexToRGBA } from '../../../helpers/common';
// import { TWITCH_FEATURE_CONTROLLER } from '../../../controller/TwitchFeatureController';
import hexRgb from 'hex-rgb';
import { COLOUR_NAME_TO_COLOUR_HEX } from '../../../../../../../helpers/Colours';
import VisualisedNode from '../VisualisedNode';
import { findSubNode } from '../../EditablePreview';
import { NODE_LIST_CONTROLLER } from '../../../../../../NodeList/NodeList';

import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollToPlugin);

const hexToRGBA = (colour, opacity = 0.7) => {
    return hexRgb(colour, {format:'css', alpha:opacity})
}

/**
 * 
 * @param {Object} obj The original object
 * @param {Object} overrideObj The object with new values to apply
 * @param {Boolean} addNewProps Only replace matching properties, or add new ones?
 * @param {Array} alternativePropNames[] An array of alternative property names to check for (if we are looking for border, but the override obj uses panelBorder: [{original: 'border', override: 'panelBorder'}])
 * @returns 
 */
const overrideObjectProps = (obj, overrideObj, addNewProps = false, alternativePropNames = [], onlyAddNew = false) => {
    const getOverrideProp = (prop) => {
        for (let i = 0; i < alternativePropNames.length; i++) {
            if (prop === alternativePropNames[i].override) {
                return alternativePropNames[i].original;
            }
        }
        return false;
    };
    for (let prop in overrideObj) {
        const overrideProp = getOverrideProp(prop) || prop;
        if (addNewProps || obj.hasOwnProperty(overrideProp)) {
            // console.log('Overriding prop: ', overrideProp, obj[overrideProp], ' with value: ', prop, overrideObj[prop]);
            if (!onlyAddNew) {
                obj[overrideProp] = overrideObj[prop];
                if (addNewProps) {
                    obj[prop] = overrideObj[prop];
                }
            } else {
                if (!obj.hasOwnProperty(overrideProp)) {
                    obj[overrideProp] = overrideObj[prop];
                }
                if (addNewProps && !obj.hasOwnProperty(prop)) {
                    obj[prop] = overrideObj[prop];
                }
            }
        }
    }
    // console.log('Overridden object: ', obj, ' with overrideObj: ', overrideObj, ' addNewProps: ', addNewProps);
    return obj;
}

const addBgStyle = (styleObj, originalCustomisationData, campaignData, overridePropNames = null) => {
    // Using our overrideObjectProps function allows us to accept alternative property names for the style object
    const customisationData = {};
    const styleObjAlternativePropNames = (overridePropNames && Array.isArray(overridePropNames)) ? overridePropNames :
    [
        {original: 'bgType', override: 'type'},
        {original: 'bgColour', override: 'colour'},
        {original: 'bgOpacity', override: 'opacity'},
        {original: 'bgGradientAngle', override: 'gradientAngle'},
        {original: 'bgGradientFrom', override: 'gradientFrom'},
        {original: 'bgGradientTo', override: 'gradientTo'},
        {original: 'bgImage', override: 'image'},
        {original: 'repeatBgImage', override: 'backgroundRepeat'},
    ];

    overrideObjectProps(customisationData, originalCustomisationData, true, styleObjAlternativePropNames);

    if (customisationData.bgType === 'solidColour') {
        styleObj.backgroundColor = hexToRGBA(COLOUR_NAME_TO_COLOUR_HEX(customisationData.bgColour, campaignData), customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1);
    } else if (customisationData.bgType === 'gradient') {
        styleObj.background = `linear-gradient(${customisationData.bgGradientAngle}deg, ${hexToRGBA(COLOUR_NAME_TO_COLOUR_HEX(customisationData.bgGradientFrom, campaignData), customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1)}, ${hexToRGBA(COLOUR_NAME_TO_COLOUR_HEX(customisationData.bgGradientTo, campaignData), customisationData.bgOpacity ? customisationData.bgOpacity / 100 : 1)})`;
    } else if (customisationData.bgType === 'image') {
        styleObj.backgroundImage = `url(${customisationData.bgImage.srcUrl})`;
        if (!styleObj.backgroundSize) {
            styleObj.backgroundSize = 'auto';
        }
        if (!styleObj.backgroundPosition) {
            styleObj.backgroundPosition = 'center';
        }
        if (!styleObj.backgroundRepeat) {
            styleObj.backgroundRepeat = customisationData.repeatBgImage ? 'repeat' : 'no-repeat';
        }
        if (customisationData.repeatBgImage === false) {
            styleObj.backgroundSize = 'contain';
        }
    } else if (customisationData.bgType === 'none') {
        styleObj.backgroundColor = 'transparent';
    }
    return styleObj;
}

const getSubNodesThrottle = {
    lastCall: 0,
}

const LiveTextBox = (props) => {
    const previewScale = props.previewScale || 1;
    const campaignData = props.campaignData;

    const allTimeoutsRef = useRef([]);

    console.log('LiveTextBox props:', props, previewScale);

    const getTemplateDataFromCustomisationData = (templateIndex) => {
        const templateData = props.customisationData.setup.templates.settings[templateIndex];
        // console.log('getTemplateDataFromCustomisationData:', templateData);
        return templateData;
    }
    // const [templateData, setTemplateData] = React.useState(getTemplateDataFromCustomisationData(props.templateIndex));
    const templateData = getTemplateDataFromCustomisationData(props.templateIndex);

    const overrideScaleable = {
        width: true,
        height: true,
    }
    if (templateData.autoHeight) {
        overrideScaleable.height = false;
    }

    const overrideVisualisedDataLinks = {
        position: props.positionDataLink,
        positionDataIndex: props.positionDataIndex,
        offsetX: props.offsetXDataLink,
        offsetXDataIndex: props.positionDataIndex,
        offsetY: props.offsetYDataLink,
        offsetYDataIndex: props.positionDataIndex,
    }

    const templatesNode = findSubNode(props?.liveTextNode?.topNode, 'Templates');
    if (Date.now() - getSubNodesThrottle.lastCall > 250) {
        if (NODE_LIST_CONTROLLER.nodeListExists('customisation-workspace-left-list')) {
            NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list', templatesNode, {boundingBoxOverflow: -10, overrideScaleable: overrideScaleable, overrideVisualisedDataLinks: overrideVisualisedDataLinks}); // Ensure sub-nodes are loaded
        } else {
            NODE_LIST_CONTROLLER.getSubNodes('customisation-workspace-left-list_' + props.node?.featureData?._id, templatesNode, {boundingBoxOverflow: -10, overrideScaleable: overrideScaleable, overrideVisualisedDataLinks: overrideVisualisedDataLinks}); // Ensure sub-nodes are loaded
        }
        getSubNodesThrottle.lastCall = Date.now();
    }
    const templateNode = templatesNode?.subNodes?.[props?.templateIndex]; // props?.liveTextNode?.topNode?.subNodes[1]?.subNodes?.[props?.templateIndex];
    templateNode.boundingBoxOverflow = -10;
    templateNode.overrideScaleable = overrideScaleable;
    templateNode.overrideVisualisedDataLinks = overrideVisualisedDataLinks;
    if (props.activeNode.parent === templatesNode) {
        props.activeNode.boundingBoxOverflow = -10;
        props.activeNode.overrideScaleable = overrideScaleable;
        props.activeNode.overrideVisualisedDataLinks = overrideVisualisedDataLinks;
    }
    // const liveControlSettings = TWITCH_FEATURE_CONTROLLER.stateContainer.state;

    const headerNode = findSubNode(templateNode, 'Header');
    const headerAutoHeight = templateData.autoSizeHeader;
    const headerOverrideScaleable = {
        width: false,
        height: true,
    }
    if (headerAutoHeight) {
        headerOverrideScaleable.height = false;
    }
    headerNode.overrideScaleable = headerOverrideScaleable;
    if (props.activeNode.name === 'Header') {
        props.activeNode.overrideScaleable = headerOverrideScaleable;
    }

    // const [preparingToRerender, setPreparingToRerender] = React.useState(false);
    // const [rerenderEntireBox, setRerenderEntireBox] = React.useState(false);

    const [headerText, setHeaderText] = React.useState(props.headerText);
    const [mainContent, setMainContent] = React.useState(props.mainContent);
    useEffect(() => {
        setHeaderText(props.headerText);
        setMainContent(props.mainContent);
    }, [props.headerText, props.mainContent]);
    // const [mainContentRenderedLength, setMainContentRenderedLength] = React.useState(0);

    const [shown, setShown] = React.useState(false);
    const checkStackingAutoHide = () => {
        /* if (!props.isSideResponse && props.customisationData.setup.settings.general[0].stackMessages) {
            LIVE_TEXT_FEATURE_CONTROLLER.timeouts.push(setTimeout(() => {
                setShown(false);
                if (props.hasSideResponse && props.responseData) {
                    props.responseData.hide = true;
                }
                LIVE_TEXT_FEATURE_CONTROLLER.timeouts.push(setTimeout(() => {
                    while (allTimeoutsRef.current.length > 0) {
                        clearTimeout(allTimeoutsRef.current.pop());
                    }
                    props.onTextBoxHidden(props.id);
                }, 500));
            }, props.customisationData.setup.settings.general[0].stackedAutoFade * 1000));
        } */
    }
    useEffect(() => {
        setTimeout(() => {
            setShown(true);
            // if (getTextAppearBehaviour() === TEXT_APPEAR_BEHAVIOURS.none) {
                checkStackingAutoHide();
            // }
        }, 10);

        // LIVE_TEXT_FEATURE_CONTROLLER.triggerAudio(`audio.messageSFX.${props.textBoxSettings.isResponse ? 'newResponse' : 'newMessage'}`, true, false, false);
    }, []);
    useEffect(() => {
        if (props.hide) {
            if (shown) {
                // LIVE_TEXT_FEATURE_CONTROLLER.triggerAudio(`audio.messageSFX.${props.textBoxSettings.isResponse ? 'hideResponse' : 'hideMessage'}`, true, false, false);
            }
            setShown(false);
            const tid = setTimeout(() => {
                endTyping();
                props.onTextBoxHidden(props.id);
            }, 500);
            // LIVE_TEXT_FEATURE_CONTROLLER.timeouts.push(tid);
        }
    }, [props.hide]);

    const TEXT_APPEAR_BEHAVIOURS = {
        'none': 'none',
        'scrolling': 'scrolling',
        'typedOut': 'typedOut',
    }
    const getTextAppearBehaviour = () => {
        if (templateData.scrollContent) {
            return TEXT_APPEAR_BEHAVIOURS.scrolling;
        }
        if (templateData.typeContent) {
            return TEXT_APPEAR_BEHAVIOURS.typedOut;
        }
        return TEXT_APPEAR_BEHAVIOURS.none;
    }
    const textAppearBehaviour = getTextAppearBehaviour();
    // const [textAppearBehaviour, setTextAppearBehaviour] = React.useState(getTextAppearBehaviour());

    // Handle typing out the text
    const [cursorPosition, setCursorPosition] = React.useState(0);
    const nextCharTimeoutId = React.useRef(null);
    const nextChar = () => {
        setCursorPosition(
            (cursorPosition) => {
                if (cursorPosition < mainContent.length) {
                    clearTimeout(nextCharTimeoutId.current);
                    nextCharTimeoutId.current = setTimeout(nextChar, (100 - templateData.scrollTypeSpeed) + 2);
                    allTimeoutsRef.current.push(nextCharTimeoutId.current);
                    
                    // Always scroll our contentBox to the end when typing out
                    gsap.to('#contentBox_' + props.id, {scrollTo: {x: 'max', y: 'max'}, ease: 'none', duration: 0.01});
                    const contentBox = document.getElementById('contentBox_' + props.id);
                    contentBox.scrollLeft = contentBox.scrollWidth + 100;
                    contentBox.scrollTop = contentBox.scrollHeight + 100;
                    // console.log('---Scrolling:', contentBox, contentBox.scrollLeft, contentBox.scrollWidth, contentBox.scrollTop, contentBox.scrollHeight);

                    // LIVE_TEXT_FEATURE_CONTROLLER.triggerAudio(`audio.messageSFX.typing`, true, true, false);
                    
                    return cursorPosition + 1;
                }

                clearTimeout(cancelCursorTimeoutId.current);
                cancelCursorTimeoutId.current = setTimeout(hideCursor, 5000);
                allTimeoutsRef.current.push(cancelCursorTimeoutId.current);
                // checkStackingAutoHide();
                
                // Always scroll our contentBox to the end when typing out
                gsap.to('#contentBox', {scrollTo: {x: 'max', y: 'max'}, ease: 'none', duration: 0.01});

                return cursorPosition;
            }
        );
    }

    const [showCursor, setShowCursor] = React.useState(false);
    const cursorBlinkTimeoutId = React.useRef(null);
    const cancelCursorTimeoutId = React.useRef(null);
    const cursorBlink = () => {
        setShowCursor(
            (showCursor) => {
                return !showCursor;
            }
        );
        clearTimeout(cursorBlinkTimeoutId.current);
        cursorBlinkTimeoutId.current = setTimeout(cursorBlink, (500 - templateData.cursorBlinkSpeed) + 10);
        allTimeoutsRef.current.push(cursorBlinkTimeoutId.current);
    }
    const hideCursor = () => {
        clearTimeout(cursorBlinkTimeoutId.current);
        setShowCursor(false);
    }

    const endTyping = () => {
        clearTimeout(nextCharTimeoutId.current);
        setCursorPosition(mainContent.length);

        hideCursor();
    }
    const startTyping = () => {
        setCursorPosition(0);
        clearTimeout(nextCharTimeoutId.current);
        nextCharTimeoutId.current = setTimeout(nextChar, 500);
        allTimeoutsRef.current.push(nextCharTimeoutId.current);

        setShowCursor(templateData.showCursor);
        if (templateData.showCursor && templateData.cursorBlink) {
            clearTimeout(cursorBlinkTimeoutId.current);
            cursorBlinkTimeoutId.current = setTimeout(cursorBlink, (500 - templateData.cursorBlinkSpeed) + 10);
            allTimeoutsRef.current.push(cursorBlinkTimeoutId.current);
        }
    }

    const [editingContent, setEditingContent] = React.useState(false);
    const editingContentRef = useRef(false);
    useEffect(() => {
        editingContentRef.current = editingContent;
    }, [editingContent]);
    useEffect(() => {
        if (props.editingTextContentData === null) {
            setEditingContent(false);
            props.forceUpdateComponent();
        }
    }, [props.editingTextContentData]);
    const startedEditingContent = () => {
        stopScrolling();
        const contentBox = document.getElementById('contentBox_' + props.id);
        if (contentBox) {
            contentBox.scrollLeft = 0;
            contentBox.scrollTop = 0;
        }
        setEditingContent(true);
    }

    const startScrollTimeoutId = React.useRef(null);
    const repeatScroll = templateData.scrollContentRepeat;
    const autoEndAfterScroll = !repeatScroll && templateData.scrollContentAutoEnd;
    const bounceRepeat = repeatScroll && templateData.scrollContentBounce;
    const addContentScrollPadding = !editingContentRef.current && templateData.scrollContent && templateData.linesSelect === 'singleLine' && (!repeatScroll || (repeatScroll && !bounceRepeat)) ? true : 0;
    const startScrolling = () => {
        // Scroll our content box, using gsap
        clearTimeout(startScrollTimeoutId.current);
        startScrollTimeoutId.current = setTimeout(() => {
            const scrollingContent = document.getElementById('scrollContent_' + props.id);
            const contentAreaSize = templateData.lineSelect === 'singleLine' ? scrollingContent.scrollWidth : scrollingContent.scrollHeight;
            gsap.to('#contentBox_' + props.id, 
                {
                    scrollTo: {x: 'max', y: 'max'}, ease: 'none', 
                    duration: contentAreaSize / (templateData.scrollTypeSpeed * 1.5) * 60,
                    repeat: repeatScroll ? -1 : 0,
                    yoyo: bounceRepeat,
                    repeatDelay: 3,
                    onComplete: autoEndAfterScroll ? autoEndAfterScrollNow : null,
                }
            );
        }, templateData.lineSelect === 'multiLine' ? 3000 : 500);
        allTimeoutsRef.current.push(startScrollTimeoutId.current);
    }
    const stopScrolling = () => {
        // Stop the scrolling
        gsap.killTweensOf('#contentBox_' + props.id);
    }
    const autoEndAfterScrollNow = () => {
        props.hideTextBox(props.textBoxIndex);
    }

    useEffect(() => {
        hideCursor();
        endTyping();
        stopScrolling();
        const contentBox = document.getElementById('contentBox_' + props.id);
        if (contentBox) {
            contentBox.scrollLeft = 0;
            contentBox.scrollTop = 0;
        }
        clearTimeout(cursorBlinkTimeoutId.current);
        clearTimeout(cancelCursorTimeoutId.current);

        const textAppearBehaviour = getTextAppearBehaviour();
        if (textAppearBehaviour === TEXT_APPEAR_BEHAVIOURS.typedOut) {
            startTyping();
        } else {
            endTyping();
        }
        if (textAppearBehaviour === TEXT_APPEAR_BEHAVIOURS.scrolling) {
            startScrolling();
        }

        setEditingContent(false);
        return () => {
            stopScrolling();
        }
    }, [props.templateIndex, props.mainContent, getTextAppearBehaviour(), repeatScroll, bounceRepeat, templateData.width, templateData.height, templateData.scrollTypeSpeed, templateData.lineSelect, templateData.cursorBlink, templateData.cursorBlinkSpeed, templateData.showCursor, templateData.autoSize, templateData.cursorChar]);

    const getTemplatePropsAsCssVars = (_templateData) => {
        // Dump all of our template properties in css vars object

        // Any props that are not strings or 'px' shouuld be set up here
        const CSS_VAR_SUFFIXES = {
            boxShadowOpacity: '%',
            bgGradientAngle: 'deg',
            bgOpacity: '%',
            headerBgGradientAngle: 'deg',
            headerBgOpacity: '%',
            headerTextShadowOpacity: '%',
            mainTextShadowOpacity: '%',
        }
        const scaleProps = [
            'width',
            'height',
            'xOffset',
            'yOffset',
            'leftImageSpacing',
            'rightImageSpacing',
            'headerHeight',
            'headerTextOutlineThickness',
            'headerTextShadowBlur',
            'headerTextShadowDistance',
            'headerEdgePadding',
            'mainTextOutlineThickness',
            'mainTextShadowBlur',
            'mainTextShadowDistance',
            'outlineThickness',
            'boxShadowBlur',
            'boxShadowDistance',
            'borderRadius',
            'extraContentPadding',
            'contentEdgePadding',
            'extraLeftPadding',
            'extraRightPadding',
            'extraTopPadding',
            'extraBottomPadding',
            'mainFontSize',
            'headerFontSize',
            'leftImageMargin',
            'rightImageMargin',
        ];
        const cssVars = {};
        for (const [key, value] of Object.entries(_templateData)) {
            // console.log('Processing template prop for css var:', key, value);
            cssVars['--' + key] = typeof value === 'number' ? (scaleProps.includes(key) ? value * previewScale : value) + (CSS_VAR_SUFFIXES[key] ? CSS_VAR_SUFFIXES[key] : 'px') : value;
        }

        // Now all our template settings are in css var, although some are no needed, but no harm them being there.
        // Below are some edge cases that need to be sorted out
        
        // Sort the shadow colour out properly as it has an opacity setting
        cssVars['--boxShadowColour'] = hexToRGBA(COLOUR_NAME_TO_COLOUR_HEX(_templateData.boxShadowColour ?? '#000000', campaignData), _templateData.boxShadowOpacity ? _templateData.boxShadowOpacity / 100 : 1);
        // Set our shadow blur and shadow distance to 0 if the shadow is not enabled
        cssVars['--boxShadowBlur'] = _templateData.addBoxShadow ? _templateData.boxShadowBlur * previewScale + 'px' : '0px';
        cssVars['--boxShadowDistance'] = _templateData.addBoxShadow ? _templateData.boxShadowDistance * previewScale + 'px' : '0px';

        // Sort out header text shadow colour because this also supports opacity
        cssVars['--headerTextShadowColour'] = hexToRGBA(COLOUR_NAME_TO_COLOUR_HEX(_templateData.headerTextShadowColour ?? '#000000', campaignData), _templateData.headerTextShadowOpacity ? _templateData.headerTextShadowOpacity / 100 : 1);
        // Set our header shadow blur and shadow distance to 0 if the shadow is not enabled
        cssVars['--headerTextShadowBlur'] = _templateData.addHeaderTextShadow ? _templateData.headerTextShadowBlur * previewScale + 'px' : '0px';
        cssVars['--headerTextShadowDistance'] = _templateData.addHeaderTextShadow ? _templateData.headerTextShadowDistance * previewScale + 'px' : '0px';
        // Set our header text outline size to 0px if the outline is not enabled
        cssVars['--headerTextOutlineThickness'] = _templateData.addHeaderTextOutline ? _templateData.headerTextOutlineThickness * previewScale + 'px' : '0px';

        // Remove bottom padding if we have no header bg as it looks weird
        cssVars['--headerPaddingBottom'] = _templateData.addHeaderBg ? _templateData.headerEdgePadding * previewScale + 'px' : '0px';

        // sort out the main text shadow colour because this also supports opacity
        cssVars['--mainTextShadowColour'] = hexToRGBA(COLOUR_NAME_TO_COLOUR_HEX(_templateData.mainTextShadowColour ?? '#000000', campaignData), _templateData.mainTextShadowOpacity ? _templateData.mainTextShadowOpacity / 100 : 1);
        // Set our main shadow blur and shadow distance to 0 if the shadow is not enabled
        cssVars['--mainTextShadowBlur'] = _templateData.addMainTextShadow ? _templateData.mainTextShadowBlur * previewScale + 'px' : '0px';
        cssVars['--mainTextShadowDistance'] = _templateData.addMainTextShadow ? _templateData.mainTextShadowDistance * previewScale + 'px' : '0px';
        // Set our main text outline size to 0px if the outline is not enabled
        cssVars['--mainTextOutlineThickness'] = _templateData.addMainTextOutline ? _templateData.mainTextOutlineThickness * previewScale + 'px' : '0px';

        // set our content box outline thickness to 0 if not enabled
        cssVars['--outlineThickness'] = _templateData.addTextBoxOutline ? _templateData.outlineThickness * previewScale + 'px' : '0px';

        // Auto sized header?
        if (_templateData.autoSizeHeader === true) {
            cssVars['--headerHeight'] = 'auto';
        }
        // Auto size the whole box?
        if (_templateData.autoHeight === true) {
            cssVars['--height'] = 'fit-content';
        }

        return cssVars;
    }
    // const [templateCssVars, setTemplateCssVars] = React.useState(getTemplatePropsAsCssVars(templateData));
    const templateCssVars = getTemplatePropsAsCssVars(templateData);
    /* const [headerBgStyle, setHeaderBgStyle] = React.useState(
        templateData.addHeaderBg ?
            addBgStyle({}, templateData, campaignData,
                [
                    {original: 'bgType', override: 'headerBgType'},
                    {original: 'bgColour', override: 'headerBgColour'},
                    {original: 'bgOpacity', override: 'headerBgOpacity'},
                    {original: 'bgGradientAngle', override: 'headerBgGradientAngle'},
                    {original: 'bgGradientFrom', override: 'headerBgGradientFrom'},
                    {original: 'bgGradientTo', override: 'headerBgGradientTo'},
                    {original: 'bgImage', override: 'headerBgImage'},
                    {original: 'repeatBgImage', override: 'headerRepeatBgImage'},
                ]
            ) : {}
    ); */
    const headerBgStyle = templateData.addHeaderBg ?
        addBgStyle({}, templateData, campaignData, 
            [
                {original: 'bgType', override: 'headerBgType'},
                {original: 'bgColour', override: 'headerBgColour'},
                {original: 'bgOpacity', override: 'headerBgOpacity'},
                {original: 'bgGradientAngle', override: 'headerBgGradientAngle'},
                {original: 'bgGradientFrom', override: 'headerBgGradientFrom'},
                {original: 'bgGradientTo', override: 'headerBgGradientTo'},
                {original: 'bgImage', override: 'headerBgImage'},
                {original: 'repeatBgImage', override: 'headerRepeatBgImage'},
            ]
        ) : {}
    /* const [mainContentBgStyle, setMainContentBgStyle] = React.useState(
        templateData.addTextBoxBg ?
            addBgStyle({}, templateData, campaignData, 
                []
            ) : {}
    ); */
    const mainContentBgStyle = templateData.addTextBoxBg ?
        addBgStyle({}, templateData, campaignData, 
            []
        ) : {}

    const extraContentSpanStyles = {};
    if (templateData.linesSelect === 'singleLine') {
        extraContentSpanStyles.whiteSpace = 'nowrap';
    }
    switch (templateData.contentTextTransform) {
        case 'all-caps':
            extraContentSpanStyles.textTransform = 'uppercase';
            break;
        case 'lower-case':
            extraContentSpanStyles.textTransform = 'lowercase';
            break;
        case 'capitalise':
            extraContentSpanStyles.textTransform = 'capitalize';
            break;
        default:
            break;
    }
    if (templateData.mainFontIsBold) {
        extraContentSpanStyles.fontWeight = 'bold';
    }
    if (templateData.mainFontIsItalic) {
        extraContentSpanStyles.fontStyle = 'italic';
    }

    const headerExtraSpanStyles = {width: props.editingTextContentData ? '100%' : 'fit-content'};
    if (templateData.headerLinesSelect === 'singleLine') {
        headerExtraSpanStyles.whiteSpace = 'nowrap';
    }
    switch (templateData.headerTextTransform) {
        case 'all-caps':
            headerExtraSpanStyles.textTransform = 'uppercase';
            break;
        case 'lower-case':
            headerExtraSpanStyles.textTransform = 'lowercase';
            break;
        case 'capitalise':
            headerExtraSpanStyles.textTransform = 'capitalize';
            break;
        default:
            break;
    }
    if (templateData.headerIsBold) {
        headerExtraSpanStyles.fontWeight = 'bold';
    }
    if (templateData.headerIsItalic) {
        headerExtraSpanStyles.fontStyle = 'italic';
    }

    const ImageComponent = (props) => {
        const scaledImageWidth = props.width ? props.width * previewScale : null;
        const scaledImageHeight = props.dimensions.height ? props.dimensions.height * previewScale : null;
        let currentMaxHeight = null;

        const imageStyles = {};

        if (!templateData.autoHeight) {
            imageStyles.maxHeight = templateData.height * previewScale + 'px';
            currentMaxHeight = templateData.height * previewScale;
        }
        if (currentMaxHeight === null || (scaledImageHeight !== null && scaledImageHeight < currentMaxHeight)) {
            imageStyles.maxHeight = scaledImageHeight ? scaledImageHeight + 'px' : 'auto';
        }
        /* if (currentMaxHeight === null && scaledImageWidth !== null) {
            imageStyles.maxWidth = scaledImageWidth + 'px';
        } */

        if (templateData[props.imageSide + 'ImageMargin']) {
            imageStyles.margin = templateData[props.imageSide + 'ImageMargin'] * previewScale + 'px';
        }
        if (props.imageSide === 'left') {
            imageStyles.marginRight = templateData.leftImageSpacing * previewScale + 'px';
        }
        if (props.imageSide === 'right') {
            imageStyles.marginLeft = templateData.rightImageSpacing * previewScale + 'px';
        }
        return (
            <div className='image-holder'>
                {(templateData[props.imageSide + 'ImageVerticalPosition'] === 'bottom' || templateData[props.imageSide + 'ImageVerticalPosition'] === 'center') &&
                    <div className='fl-grow'></div>
                }
                <div className='fl-shrink'>
                    <img style={imageStyles} src={props.src} alt={props.alt} onLoad={props.onLoad} />
                </div>
                {(templateData[props.imageSide + 'ImageVerticalPosition'] === 'top' || templateData[props.imageSide + 'ImageVerticalPosition'] === 'center') &&
                    <div className='fl-grow'></div>
                }
            </div>
        );
    }

    let transitionClassList = '';
    /* let transitionClassList = 'text-box-animation';
    // We always add a fade!
    transitionClassList += ` tb-fade-${shown ? 'in' : 'out'}`; */

    const appearTween = /* liveControlSettings.featureControl.overrideAppearTween ? liveControlSettings.featureControl.appearTween : */ templateData.appearTween;
    const bumpEase = /* liveControlSettings.featureControl.overrideAppearTween ? liveControlSettings.featureControl.bumpEase : */ templateData.bumpEase;

    /* switch (appearTween) {
        case 'pop':
            transitionClassList += ` tb-pop-${shown ? 'in' : 'out'}`;
            break;
        case 'slideBottom':
            transitionClassList += ` tb-slide-bottom-${shown ? 'in' : 'out'}`;
            break;
        case 'slideTop':
            transitionClassList += ` tb-slide-top-${shown ? 'in' : 'out'}`;
            break;
        case 'slideLeft':
            transitionClassList += ` tb-slide-left-${shown ? 'in' : 'out'}`;
            break;
        case 'slideRight':
            transitionClassList += ` tb-slide-right-${shown ? 'in' : 'out'}`;
            break;
        default:
            break;
    }
    if (bumpEase) {
        transitionClassList += ` tb-back-ease-${shown ? 'in' : 'out'}`;
    } */

    let contentIsScrollable = false;
    let contentScrollAxis = 'y';

    if (textAppearBehaviour === TEXT_APPEAR_BEHAVIOURS.typedOut || textAppearBehaviour === TEXT_APPEAR_BEHAVIOURS.scrolling) {
        contentIsScrollable = true;
        if (templateData.linesSelect === 'singleLine') {
            contentScrollAxis = 'x';
        } else {
            contentScrollAxis = 'y';
        }
    }

    // In order to make the content box scroll properly, we need to know the exact width / height of the content area
    const [contentAreaWidth, setContentAreaWidth] = React.useState(0);
    const [contentAreaHeight, setContentAreaHeight] = React.useState(0);
    const getContentAreaSize = () => {
        const contentBox = document.getElementById('contentArea_' + props.id);
        if (contentBox) {
            const contentAreaWidth = contentBox.scrollWidth - templateData.contentEdgePadding * previewScale * 2;
            const contentAreaHeight = contentBox.scrollHeight - templateData.contentEdgePadding * previewScale * 2;
            const bounds = contentBox.getBoundingClientRect();
            // console.log('Content Area Size:', contentAreaWidth, contentAreaHeight, bounds, contentBox);
            setContentAreaHeight(contentAreaHeight);
            setContentAreaWidth(contentAreaWidth);
            // setTimeout(getContentAreaSize, 1000);
        } else {
            // console.log('Content Area Size: Content Box not found');
            setTimeout(getContentAreaSize, 10);
        }
        /* if (!shown) {
            setTimeout(getContentAreaSize, 1000);
        } */
    }

    const leftImageLoaded = useRef(!templateData.addLeftImage);
    const rightImageLoaded = useRef(!templateData.addRightImage);
    const [leftImageDimensions, setLeftImageDimensions] = React.useState({width: 0, height: 0});
    const [rightImageDimensions, setRightImageDimensions] = React.useState({width: 0, height: 0});
    const onLeftImageLoad = (e) => {
        if (!leftImageLoaded.current) {
            leftImageLoaded.current = true;
            setLeftImageDimensions({width: e.target.naturalWidth, height: e.target.naturalHeight});
            if (leftImageLoaded && rightImageLoaded) {
                getContentAreaSize();
            }
        }
    }
    const onRightImageLoad = (e) => {
        if (!rightImageLoaded.current) {
            rightImageLoaded.current = true;
            setRightImageDimensions({width: e.target.naturalWidth, height: e.target.naturalHeight});
            if (leftImageLoaded && rightImageLoaded) {
                getContentAreaSize();
            }
        }
    }

    useEffect(() => {
        leftImageLoaded.current = !templateData.addLeftImage;
        rightImageLoaded.current = !templateData.addRightImage;
        setContentAreaWidth(0);
        setContentAreaHeight(0);
        setTimeout(() => {
            getContentAreaSize();
        }, 10);
    }, [templateData, templateData.addLeftImage, templateData.addRightImage, templateData.leftImageFile.srcUrl, templateData.rightImageFile.srcUrl, templateData.width, templateData.height, leftImageDimensions.width, leftImageDimensions.height, rightImageDimensions.width, rightImageDimensions.height]);

    extraContentSpanStyles.width = contentAreaWidth + 'px';
    extraContentSpanStyles.minWidth = contentAreaWidth + 'px';
    extraContentSpanStyles.maxWidth = contentAreaWidth + 'px';
    extraContentSpanStyles.height = (templateData.autoHeight || templateData.linesSelect === 'singleLine') ? 'fit-content' : contentAreaHeight * previewScale + 'px';
    extraContentSpanStyles.minHeight = (templateData.autoHeight || templateData.linesSelect === 'singleLine') ? 'fit-content' : contentAreaHeight * previewScale + 'px';
    extraContentSpanStyles.maxHeight = (templateData.autoHeight || templateData.linesSelect === 'singleLine') ? 'fit-content' : contentAreaHeight * previewScale + 'px';

    templateNode.dontVisualiseModuleSettings = {
        position: true,
        dimensions: true,
    };

    useEffect(() => {
        console.log('Livetextbox activeNode useEffect props: ', props);
    }, [props.activeNode]);

    return (
        <div className={`${props.hasSideResponse ? 'lt-side-by-side-response-holder' : ''}${props.isSideResponse ? 'lt-side-by-side-response' : ''}${!props.isSideResponse && (props.textBoxSettings.isResponse || props.isResponse)? 'lt-is-below-response' : ''}`}>
            <div className={`live-text-holder${props.hasSideResponse ? ' lt-has-side-response' : ''}`} style={templateCssVars}>
                
                <div className='lt-fl-col'>
                    {props.addStackingGap && <div className="live-text-stack-spacer"></div>}

                    <VisualisedNode
                        {...props}
                        node={templateNode}
                        dataIndex={props?.templateIndex}
                        style={{...templateCssVars, width: 'fit-content', height: 'fit-content', margin: `-10px`, padding: '10px'}}
                    >
                        <div className={`live-text-box-holder ${transitionClassList}`}>
                            {templateData.addLeftImage && templateData.leftImageOutside && templateData.leftImageFile?.srcUrl &&
                                <VisualisedNode
                                    {...props}
                                    node={findSubNode(templateNode, 'Left Image')}
                                    style={{width: 'fit-content', height: '100%'}}
                                >
                                    <ImageComponent imageSide='left' src={templateData.leftImageFile.srcUrl} dimensions={leftImageDimensions} alt="Left Image" onLoad={!leftImageLoaded.current ? onLeftImageLoad : null} />
                                </VisualisedNode>
                            }
                            {/* <VisualisedNode
                                {...props}
                                node={templateNode}
                                style={{width: 'fit-content', height: 'fit-content'}}
                            > */}
                            <div id="liveTextBox" className="live-text-box"
                                style={/* (!templateData.addHeader || !templateData.addHeaderBg) ? */ mainContentBgStyle /* : {} */}
                            >
                                {templateData.addLeftImage && !templateData.leftImageOutside && templateData.leftImageFile?.srcUrl &&
                                    <VisualisedNode
                                        {...props}
                                        node={findSubNode(templateNode, 'Left Image')}
                                        style={{width: 'fit-content', height: '100%'}}
                                    >
                                        <ImageComponent imageSide='left' src={templateData.leftImageFile.srcUrl} dimensions={leftImageDimensions} alt="Left Image" onLoad={!leftImageLoaded.current ? onLeftImageLoad : null} />
                                    </VisualisedNode>
                                }
                                <div className='content-holder'>
                                    {templateData.addHeader &&
                                        
                                        <div className="header-text"
                                            style={templateData.addHeaderBg ? headerBgStyle : {}}
                                        >
                                            <VisualisedNode
                                                {...props}
                                                node={findSubNode(templateNode, 'Header')}
                                                style={{
                                                    width: '100%', height: '100%',
                                                    padding: 'var(--headerEdgePadding) var(--headerEdgePadding) var(--headerPaddingBottom) var(--headerEdgePadding)',
                                                }}
                                            >
                                                {(templateData.headerVerticalPosition === 'bottom' || templateData.headerVerticalPosition === 'center') &&
                                                    <div className='fl-grow'></div>
                                                }
                                                <div className='fl-shrink'>
                                                    {/* &nbsp; is a non-breaking space. It means we have text in the box at all times, ensuring the box doesn't try to resize when we don't want it to */}
                                                    {(contentAreaWidth === 0 || props.presetTextNode === null) &&
                                                        <span style={headerExtraSpanStyles}>{contentAreaWidth === 0 ? <>&nbsp;</> : props.headerText}</span>
                                                    }
                                                    {contentAreaWidth !== 0 && props.presetTextNode !== null &&
                                                        <span style={headerExtraSpanStyles}>
                                                            <VisualisedNode
                                                                {...props}
                                                                node={props.presetTextNode.parent}
                                                                textEditMode={true}
                                                                textEditModeFieldPath={'setup.presetText.presets.headerText'}
                                                                textEditModeContentNode={props.presetTextNode}
                                                                multiline={true}
                                                                index={props.presetTextIndex}
                                                            >
                                                                {/*lastPoll.pollData.question*/}
                                                            </VisualisedNode>
                                                        </span>
                                                    }
                                                </div>
                                                {(templateData.headerVerticalPosition === 'top' || templateData.headerVerticalPosition === 'center') &&
                                                    <div className='fl-grow'></div>
                                                }
                                            </VisualisedNode>
                                        </div>
                                        
                                    }
                                    
                                    <div id={"contentArea_" + props.id} className="main-content"
                                        style={templateData.addHeader && templateData.addHeaderBg ? mainContentBgStyle : {}}
                                    >
                                        <VisualisedNode
                                            {...props}
                                            node={findSubNode(templateNode, 'Content')}
                                            style={{
                                                width: 'fit-content', height: '100%',
                                                padding: 'var(--contentEdgePadding)',
                                            }}
                                        >
                                            {!templateData.autoHeight && (templateData.contentVerticalPosition === 'bottom' || templateData.contentVerticalPosition === 'center') &&
                                                <div className='fl-grow'></div>
                                            }
                                            <div className='fl-shrink'>
                                                {contentAreaWidth !== 0 && props.presetTextNode === null &&
                                                    <div id={'contentBox_' + props.id} className={`${contentIsScrollable ? 'lt-scrollable-' + contentScrollAxis : ''}`} style={extraContentSpanStyles}>
                                                        <div className='content' id={"scrollContent_" + props.id} style={{paddingLeft: addContentScrollPadding ? contentAreaWidth : 0, marginRight: addContentScrollPadding ? contentAreaWidth : 0}}>
                                                            {props.mainContent.substr(0, cursorPosition)}{showCursor && templateData.cursorChar}
                                                            {/* &nbsp; is a non-breaking space. It means we have text in the box at all times, ensuring the box doesn't try to resize when we don't want it to */}
                                                            <>&nbsp;</>
                                                        </div>
                                                    </div>
                                                }
                                                {contentAreaWidth !== 0 && props.presetTextNode !== null &&
                                                    <div id={'contentBox_' + props.id} className={`${contentIsScrollable ? 'lt-scrollable-' + contentScrollAxis : ''}`} style={extraContentSpanStyles}>
                                                        <div className='content' id={"scrollContent_" + props.id} style={{paddingLeft: addContentScrollPadding ? contentAreaWidth : 0, marginRight: addContentScrollPadding ? contentAreaWidth : 0, width: editingContent ? '100%' : 'fit-content'}}>
                                                            <VisualisedNode
                                                                {...props}
                                                                node={props.presetTextNode.parent}
                                                                textEditMode={true}
                                                                textEditModeFieldPath={'setup.presetText.presets.mainContent'}
                                                                textEditModeContentNode={props.presetTextNode}
                                                                multiline={true}
                                                                index={props.presetTextIndex}
                                                                content={props.mainContent.substr(0, cursorPosition) + (showCursor ? templateData.cursorChar : '')}
                                                                textEditStartedCallback={startedEditingContent}
                                                            >
                                                                {/*mainContent.substr(0, cursorPosition)}{showCursor && templateData.cursorChar*/}
                                                                {/* &nbsp; is a non-breaking space. It means we have text in the box at all times, ensuring the box doesn't try to resize when we don't want it to */}
                                                                <>&nbsp;</>
                                                            </VisualisedNode>
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                            {!templateData.autoHeight && (templateData.contentVerticalPosition === 'top' || templateData.contentVerticalPosition === 'center') &&
                                                <div className='fl-grow'></div>
                                            }
                                        </VisualisedNode>
                                    </div>
                                    
                                </div>
                                {templateData.addRightImage && !templateData.rightImageOutside && templateData.rightImageFile?.srcUrl &&
                                    <VisualisedNode
                                        {...props}
                                        node={findSubNode(templateNode, 'Right Image')}
                                        style={{width: 'fit-content', height: '100%'}}
                                    >
                                        <ImageComponent imageSide='right' src={templateData.rightImageFile.srcUrl} dimensions={rightImageDimensions} alt="Right Image" onLoad={!rightImageLoaded.current ? onRightImageLoad : null} />
                                    </VisualisedNode>
                                }
                            </div>
                            {/* </VisualisedNode> */}
                            {templateData.addRightImage && templateData.rightImageOutside && templateData.rightImageFile?.srcUrl &&
                                <VisualisedNode
                                    {...props}
                                    node={findSubNode(templateNode, 'Right Image')}
                                    style={{width: 'fit-content', height: '100%'}}
                                >
                                    <ImageComponent imageSide='right' src={templateData.rightImageFile.srcUrl} dimensions={rightImageDimensions} alt="Right Image" onLoad={!rightImageLoaded.current ? onRightImageLoad : null} />
                                </VisualisedNode>
                            }
                        </div>
                    </VisualisedNode>
                </div>
            </div>
            {props.hasSideResponse && props.responseData &&
                <LiveTextBox
                    {...props}
                    customisationData={props.customisationData}
                    templateIndex={props.responseData.templateIndex}
                    headerText={props.responseData.headerText}
                    mainContent={props.responseData.mainContent}
                    hide={props.responseData.hide}
                    textBoxIndex={props.textBoxIndex + 1}
                    onTextBoxHidden={props.onTextBoxHidden}
                    textBoxSettings={props.responseData}
                    isResponse={props.generalSettings.responseStyle !== 'side'}
                    isSideResponse={props.generalSettings.responseStyle === 'side'}
                    hasSideResponse={false}
                    responseData={props.responseData}
                />
            }
        </div>
    );
}

export default LiveTextBox;
